import React, { useCallback, useId, useState } from 'react';

import { Checkbox } from '@opendoor/atoms/checkbox';
import { Collapsible } from '@opendoor/atoms/collapsible';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';

export interface MarketingOptInProps {
  /** Controlled checked state. */
  checked: boolean;
  /** Fires with the next checked state on user toggle. */
  onCheckedChange: (checked: boolean) => void;
  /** Bold header above the description; the checkbox's accessible name. */
  label: string;
  /** The part of the consent language always on screen. */
  description: string;
  /**
   * Trailing consent language folded behind a "more" control, revealed with an
   * animated height. Omit (or pass `''`) to render `description` whole with no
   * control. Both together are the full versioned string — see
   * `splitConsentDescription` — and both are referenced by the checkbox's
   * `aria-describedby`, so the fold never shortens what it is described as.
   */
  detail?: string;
  /** Analytics name forwarded to the underlying checkbox. */
  analyticsName?: string;
  /**
   * The checkbox's value, which atoms also sends as the click event's
   * `optionName`. Required because journey rejects the event outright when it is
   * empty, and a dropped event is invisible from here.
   */
  value: string;
  className?: string;
}

/**
 * Checkbox + bold label + consent description, composed from atoms — there is no
 * atoms checkbox/label/description composite that stacks them beside the control
 * (`Form.Checkbox` renders both above it), and the label names the control while
 * the description is wired as its description rather than folding the whole legal
 * sentence into the accessible name.
 *
 * Copy is required, never defaulted: every consent shown has to come from a
 * versioned `SmsConsentSpec` entry, so the `consentLanguage` recorded for it
 * names the words actually rendered. `PhoneConsentFields` renders one of these
 * per checkbox in a spec.
 *
 * `detail` is progressive disclosure, not truncation: the folded run stays in the
 * document and one control away for every user, sighted or not, and the recorded
 * version still names the whole string. It exists because the carrier boilerplate
 * is identical on every box and buries the consent that differs between them once
 * several stack.
 */
export const MarketingOptIn: React.FC<MarketingOptInProps> = ({
  checked,
  onCheckedChange,
  label,
  description,
  detail,
  analyticsName,
  value,
  className,
}) => {
  const controlId = useId();
  const descriptionId = useId();
  const detailId = useId();
  const [expanded, setExpanded] = useState(false);

  // Drop the checkbox's second event-details arg so consumers get the clean
  // single-boolean contract (and a form binding's `handleChange` isn't handed
  // an unexpected second positional argument).
  const handleCheckedChange = useCallback(
    (next: boolean) => {
      onCheckedChange(next);
    },
    [onCheckedChange],
  );

  const hasDetail = detail != null && detail.length > 0;

  return (
    <div className={cn('flex w-full items-start gap-3', className)}>
      <Checkbox
        id={controlId}
        size="sm"
        checked={checked}
        onCheckedChange={handleCheckedChange}
        // Both halves, so the description is the whole versioned string in
        // either fold state — folding changes what is on screen, not what the
        // control is described as agreeing to.
        aria-describedby={hasDetail ? `${descriptionId} ${detailId}` : descriptionId}
        analyticsName={analyticsName}
        value={value}
        className="mt-px shrink-0"
      />
      <div className="flex flex-1 flex-col gap-1">
        <Text
          as="label"
          htmlFor={controlId}
          className="cursor-pointer text-label-sm text-od-content-primary"
        >
          {label}
        </Text>
        {hasDetail ? (
          <Collapsible.Root open={expanded} onOpenChange={setExpanded}>
            <Text className="text-body-sm text-od-content-tertiary">
              {/* The id sits on the text alone, not the paragraph: the trigger
                  is a descendant, and an `aria-describedby` pointed at the
                  paragraph would read the control's own label out as part of
                  the consent sentence. */}
              <span id={descriptionId}>{description}</span>{' '}
              {/* Inline so the control reads as the end of the sentence it
                  continues. The accessible name leads with the visible word and
                  appends which disclosure it belongs to, rather than replacing
                  it — an `aria-label` of its own would fail WCAG 2.5.3 (Label in
                  Name) the same way legacy's consent links did. */}
              <Collapsible.Trigger className="inline appearance-none border-0 bg-transparent p-0 text-body-sm text-od-content-primary underline focus:outline-none focus-visible:outline-solid focus-visible:outline-1">
                {/* The separating space is its own text node: inside the
                    sr-only span a leading space is collapsed away by the
                    accessible-name computation, which runs the two words
                    together ("moreof the …"). */}
                {expanded ? 'less' : 'more'}{' '}
                <span className="sr-only">{`of the ${label} disclosure`}</span>
              </Collapsible.Trigger>
            </Text>
            {/* `hiddenUntilFound`, not `keepMounted`: it supersedes it, keeping
                the panel mounted AND hiding it with `hidden="until-found"` so
                find-in-page still reaches the folded words (Ctrl+F for "Reply
                HELP" reveals it). `keepMounted` on its own leaves the copy in
                the document under plain `hidden`, which is unsearchable — and
                skips the first open's height transition, since Base UI only
                persists the closed `data-starting-style` under
                `hiddenUntilFound`. */}
            <Collapsible.Panel hiddenUntilFound className="auth-consent-detail">
              <Text as="p" id={detailId} className="pt-1 text-body-sm text-od-content-tertiary">
                {detail}
              </Text>
            </Collapsible.Panel>
          </Collapsible.Root>
        ) : (
          <Text id={descriptionId} className="text-body-sm text-od-content-tertiary">
            {description}
          </Text>
        )}
      </div>
    </div>
  );
};
